import React, { useRef } from "react";
import { clsx } from "clsx";
import { Badge } from "src/components/base/badge";
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";

/**
 * One entry in the tag input. Mutated by the consumer to reflect server-side state after a
 * submit attempt.
 */
export type DomainChip = {
    /** The user-entered domain name. */
    name: string;
    /** Set once the domain has been created server-side. */
    uuid?: string;
    /** "pending" until submitted; "ok" / "error" after the API call resolves. */
    status: "pending" | "ok" | "error";
    /** Localised error message when `status === "error"`. */
    error?: string;
};

/**
 * Properties for {@link DomainTagInput}.
 */
export type DomainTagInputProps = {
    /** Current chip list. */
    chips: DomainChip[];
    /** Replaces the chip list. */
    onChange: (chips: DomainChip[]) => void;
    /** Current text in the input box that hasn't been turned into a chip yet. */
    draft: string;
    /** Updates the draft text. */
    onDraftChange: (draft: string) => void;
    /** Placeholder shown only while the chip list is empty. */
    placeholder?: string;
    /** Aria label for each chip's remove button (i18n-supplied). */
    removeAriaLabel: string;
    /** Whether the inner text input should auto-focus when mounted. */
    autoFocus?: boolean;
};

/**
 * Tag-style input for collecting one or more domain names. Each chip carries a status so the
 * caller can render per-domain success/error feedback after submitting.
 *
 * The visual chrome (rounded surface, border, focus ring, dark-mode bg) mirrors the standard
 * `<Input>` component so it sits cleanly next to other form controls.
 *
 * @param props see {@link DomainTagInputProps}
 * @returns the rendered tag input
 */
export function DomainTagInput(props: DomainTagInputProps) {
    const { chips, onChange, draft, onDraftChange, placeholder, removeAriaLabel, autoFocus } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    const commit = () => {
        const result = commitDraft(draft, chips);
        if (result.draft !== draft) onDraftChange(result.draft);
        if (result.chips !== chips) onChange(result.chips);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === "Enter" || e.key === "," || e.key === " ") {
            if (draft.trim().length > 0) {
                e.preventDefault();
                commit();
            }
        } else if (e.key === "Backspace" && draft.length === 0 && chips.length > 0) {
            e.preventDefault();
            onChange(chips.slice(0, -1));
        }
    };

    const removeChip = (index: number) => {
        onChange(chips.filter((_, i) => i !== index));
    };

    return (
        <span
            data-slot={"control"}
            className={clsx(
                // Layout matches base Input: outer span carries the layered surface so before/after pseudo elements
                // can drive the white bg + shadow (light mode) and the focus ring (sm and up).
                "relative block w-full",
                "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
                "dark:before:hidden",
                "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
            )}
        >
            <div
                onClick={() => inputRef.current?.focus()}
                className={clsx(
                    "relative flex min-h-10 w-full flex-wrap items-center gap-1.5",
                    // Same shape + padding (offset by -1px to compensate for the border) as base Input.
                    "appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                    "border border-zinc-950/10 hover:border-zinc-950/20 dark:border-white/10 dark:hover:border-white/20",
                    "bg-transparent dark:bg-white/5",
                    "cursor-text text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white",
                )}
            >
                {chips.map((chip, idx) => (
                    <Badge
                        key={`${chip.name}-${idx}`}
                        color={chip.status === "ok" ? "green" : chip.status === "error" ? "red" : "zinc"}
                        title={chip.error}
                    >
                        {chip.status === "ok" && <CheckCircleIcon className={"size-3"} />}
                        {chip.status === "error" && <XCircleIcon className={"size-3"} />}
                        <span>{chip.name}</span>
                        <button
                            type={"button"}
                            aria-label={removeAriaLabel}
                            onClick={(e) => {
                                e.stopPropagation();
                                removeChip(idx);
                            }}
                            className={"ml-0.5 cursor-pointer rounded-sm hover:opacity-70"}
                        >
                            <XMarkIcon className={"size-3"} />
                        </button>
                    </Badge>
                ))}
                <input
                    ref={inputRef}
                    autoFocus={autoFocus}
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={commit}
                    placeholder={chips.length === 0 ? placeholder : ""}
                    className={
                        "min-w-32 flex-1 border-0 bg-transparent p-0 text-base/6 text-zinc-950 placeholder:text-zinc-500 focus:ring-0 focus:outline-none sm:text-sm/6 dark:text-white"
                    }
                />
            </div>
        </span>
    );
}

/**
 * Result of {@link commitDraft}: the cleared draft and the new chip list.
 */
export type CommitDraftResult = {
    /** The draft is always reset to "" after committing. */
    draft: string;
    /** The chip list, with new chips appended (or unchanged if nothing committed). */
    chips: DomainChip[];
};

/**
 * Push the current draft (one or more comma/whitespace-separated names) onto the chip list,
 * skipping empties and duplicates.
 *
 * @param draft current text in the input box
 * @param chips current chip list
 * @returns cleared draft and the new chip list (see {@link CommitDraftResult})
 */
export function commitDraft(draft: string, chips: DomainChip[]): CommitDraftResult {
    const tokens = draft
        .split(/[\s,]+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

    if (tokens.length === 0) return { draft: "", chips };

    const existing = new Set(chips.map((c) => c.name));
    const additions: DomainChip[] = [];
    for (const token of tokens) {
        if (existing.has(token)) continue;
        existing.add(token);
        additions.push({ name: token, status: "pending" });
    }

    return { draft: "", chips: additions.length ? [...chips, ...additions] : chips };
}
